import { useMemo } from 'react';
import { camelCase } from 'lodash';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';

import { districtSelectors } from '@/store/selectors';

import useGlobalSettings from '../useGlobalSettings';

import { getApiUrl, renameObjectKeys } from '@/utils/lib';
import getValuesFromObject from '@/utils/getValuesFromObject';

import { FOOTER_GLOBAL_LOCAL_SETTINGS_VALUES } from './consts';

import { TDistrictSettings } from '@/types/district-settings';
import { TGlobalSettings } from '@/types/global-settings';
import { TPortalParams } from '@/types/portal-params';
import { TUseFooterGlobalLocalSettings } from './types';

const { getDistrictSettings, getDistrictDetails } = districtSelectors;

const useFooterGlobalLocalSettings = (): TUseFooterGlobalLocalSettings => {
  const { prefix } = useParams<TPortalParams>();

  const globalSettings: TGlobalSettings = useGlobalSettings();
  const districSettings: TDistrictSettings = useSelector(getDistrictSettings);

  const districDetails = useSelector(state =>
    getDistrictDetails(state, prefix)
  ).get('data');

  const rpoLogo = districDetails?.getIn(['properties', 'rpo_logo']);
  const isGlobalRpoLogo = districDetails?.getIn([
    'properties',
    'use_global_rpo_logo'
  ]);

  const globalFooterSettingsEnabled =
    districSettings?.global_footer_settings_enabled;

  const rpoLogoValue = useMemo(
    () =>
      isGlobalRpoLogo || !prefix
        ? { rpo_logo: globalSettings.rpo_logo }
        : { rpo_logo: `${getApiUrl()}${rpoLogo}` },
    [globalSettings.rpo_logo, isGlobalRpoLogo, prefix, rpoLogo]
  );

  const footerValues = useMemo(
    () =>
      globalFooterSettingsEnabled || !prefix
        ? { ...globalSettings, ...rpoLogoValue }
        : { ...districSettings, ...rpoLogoValue },
    [
      rpoLogoValue,
      districSettings,
      globalFooterSettingsEnabled,
      globalSettings,
      prefix
    ]
  );

  const memoizedValues = useMemo(() => {
    const pickedValues = getValuesFromObject(
      FOOTER_GLOBAL_LOCAL_SETTINGS_VALUES,
      footerValues
    );

    return renameObjectKeys(pickedValues, camelCase);
  }, [footerValues]) as TUseFooterGlobalLocalSettings;

  return memoizedValues;
};

export default useFooterGlobalLocalSettings;
